import MarketplaceStore from "../../infrastructure/store/marketplace-store";
import {DocumentMetadataResponse} from "../../entity/resource/document-metadata-response";
import {MarketplaceSearchDto} from "../../entity/dto/marketplace-search-dto";
import {DocumentMetadataRequestDto} from "../../entity/dto/document-metadata-request-dto";
import {toast} from "react-toastify";
import {FilterMetadataRequestDto} from "../../entity/dto/filter-metadata-request-dto";
import {FilterMetadataResponse} from "../../entity/resource/filter-metadata-response";
import {MarketplaceRecordRequestDto} from "../../entity/dto/marketplace-record-request-dto";
import {MarketplaceRecordResponse} from "../../entity/resource/marketplace-record-response";
import {MetadataType} from "../../entity/enums/metadata-type";

export default class MarketplaceService {
    constructor(private marketplaceStore: MarketplaceStore) {}

    getDocumentMetadata = async (metadataType: MetadataType, stateID: string, countyID: string, indexType?: string, dateRange?: string): Promise<DocumentMetadataResponse | undefined>  => {
        const payload: DocumentMetadataRequestDto = {
            metadataType: metadataType,
            stateID: stateID,
            countyID: countyID,
            ...(indexType && { indexType }),
            ...(dateRange && { dateRange })
        };
        const response = await this.marketplaceStore.getDocumentMetadata(payload);

        if (typeof response === 'string') {
            toast.error(response);
            return;
        } else
            return response;
    }

    filterMetaData = async (payload: FilterMetadataRequestDto): Promise<FilterMetadataResponse[] | undefined> => {
        const response = await this.marketplaceStore.filterMetaData(payload);

        if (typeof response === 'string') {
            toast.error(response);
            return;
        } else
            return response;
    }

    createMarketplaceRecord = async (organizationID: string, payload: MarketplaceRecordRequestDto): Promise<MarketplaceRecordResponse | undefined | string> => {
        return await this.marketplaceStore.createMarketplaceRecord(organizationID, payload);
    }

    downloadMarketplaceDocument = async (organizationId: string, batchID: string): Promise<string | undefined> => {
        console.info(`[MarketplaceService] downloadMarketplaceDocument called with organizationId: ${organizationId}, batchID: ${batchID}`);

        try {
            const response = await this.marketplaceStore.downloadMarketplaceDocument(organizationId, batchID);

            if (typeof response === 'string') {
                console.error(`[MarketplaceService] Error response received: ${response}`);
                toast.error(response);
                return undefined;
            }

            if (!response.success) {
                console.error(`[MarketplaceService] Backend returned failure for batchID: ${batchID}, message: ${response.message}`);
                toast.error(response.message || 'Failed to generate download URL');
                return undefined;
            }

            console.info(`[MarketplaceService] Successfully received download URL for batchID: ${batchID}, expires in: ${response.expiresIn}`);
            return response.downloadUrl;
        } catch (error) {
            console.error(`[MarketplaceService] Exception in downloadMarketplaceDocument for batchID: ${batchID}`, error);
            toast.error('Failed to get download URL. Please try again.');
            return undefined;
        }
    }

    showTimeoutMessage = (): void => {
        toast.info('Processing your download. Please refresh to check the progress.');
    }

    showImportTimeoutMessage = (): void => {
        toast.info('Processing your import. Please refresh to check the progress.');
    }

    getMarketplaceSearchDto = (): MarketplaceSearchDto => {
        return {
            selectedState: '',
            selectedCounty: '',
            selectedBookType: '',
            selectedIndexType: '',
            dateRange: '',
            selectedIndexBook: '',
            selectedRecordedBook: '',
            pageFrom: '',
            pageTo: ''
        };
    }

    downloadFile = async (downloadUrl: string, filename: string): Promise<void> => {
        console.info(`[MarketplaceService] downloadFile called with filename: ${filename}, URL: ${downloadUrl.substring(0, 50)}...`);

        if (!downloadUrl || !downloadUrl.startsWith('http')) {
            throw new Error('Invalid download URL provided');
        }

        const safeFilename = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;

        console.info(`[MarketplaceService] Fetching blob for: ${safeFilename}`);

        const response = await fetch(downloadUrl);

        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = safeFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Free memory
        URL.revokeObjectURL(url);

        console.info(`[MarketplaceService] Download completed for: ${safeFilename}`);
        toast.success('Document downloaded successfully!');

    }
}
